import {
  Block,
  BlockProps,
  BorderStyleProperty,
  Button,
  Flex,
  Typography,
} from '@aloft/toolkit-sui';

import { PropsWithChildren, ReactElement } from 'react';

import { useModuleContext } from '../modules/shared/moduleContext/useModuleContext.ts';
import { borderColor, borderStyle, borderWidth } from '../styles/border.ts';

type ModuleContainerProps = PropsWithChildren<{
  title?: string;
  compact?: boolean;
  overflow?: BlockProps['overflow'];
  forwardRoute?: string;
  backwardRoute?: string;
  component?: ReactElement;
  moduleID: string;
  hideTitleBorderBottom?: boolean;
}>;

export const ModuleContainer = ({
  children,
  title,
  compact,
  overflow = 'auto',
  forwardRoute,
  backwardRoute,
  component,
  moduleID,
  hideTitleBorderBottom = false,
}: ModuleContainerProps) => {
  const {
    navigation: { useParams, useNavigate },
  } = useModuleContext();
  const navigate = useNavigate();
  const { orderId } = useParams();

  return (
    <Flex
      id={`${moduleID}-module`}
      flexDirection="column"
      height="100%"
      width="100%"
      position="relative"
      marginBottom={forwardRoute || backwardRoute ? '1rem' : undefined}
      backgroundColor="surface1"
    >
      <Flex
        flexDirection="column"
        flexGrow={1}
        padding={compact ? '0' : '10px'}
        justifyContent="flex-start"
        alignItems="flex-start"
        overflow={title ? 'hidden' : overflow}
        width="100%"
        backgroundColor="surface1"
      >
        {title ? (
          <>
            <Block
              width="100%"
              paddingLeft={12}
              paddingY={3}
              {...(!hideTitleBorderBottom && {
                borderBottomWidth: '0.75px',
                borderStyle: 'solid',
                borderColor: 'surface4',
              })}
            >
              <Typography variant="h1" __dangerDoNotUseSx={{ fontSize: '24px' }}>
                {title}
              </Typography>
            </Block>
            <Block
              width="100%"
              flexGrow={1}
              backgroundColor="surface6"
              overflow={title ? overflow : 'hidden'}
            >
              {children}
            </Block>
          </>
        ) : (
          children
        )}
      </Flex>
      {(forwardRoute || backwardRoute) && (
        <Flex
          padding={3}
          alignItems="center"
          flexShrink={1}
          flexGrow={0}
          borderTopWidth={borderWidth}
          borderColor={borderColor}
          borderStyle={borderStyle as BorderStyleProperty}
          backgroundColor="surface1"
        >
          {backwardRoute && (
            <Button
              text="Back"
              variant="secondary"
              onClick={() => navigate(`/order/${orderId}/${backwardRoute}`)}
              marginLeft=".5rem"
            />
          )}
          {forwardRoute && (
            <Button
              text="Next"
              variant="secondary"
              onClick={() => navigate(`/order/${orderId}/${forwardRoute}`)}
              marginLeft=".5rem"
            />
          )}
          {component && component}
          <Block marginRight=".5rem" />
        </Flex>
      )}
    </Flex>
  );
};
